// src/pages/SalesMenu.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Typography,
  ButtonBase,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// أيقونات Material-UI المستوردة
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PercentIcon from "@mui/icons-material/Percent";
import FactCheckIcon from "@mui/icons-material/FactCheck"; 

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function SalesMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth(); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.language === 'ar';
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? parseInt(storedCompanyId, 10) : null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setError(t('salesMenu.companyIdNotFound'));
      return;
    }
    setLoading(true);
    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then((res) => setInvoiceTypes(res.data || []))
      .catch((err) => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        setError(t('salesMenu.failedToLoadInvoiceTypes'));
      })
      .finally(() => setLoading(false));
  }, [companyId, t]);

  // دالة مساعدة لتحديد التكوين اللوني والبصري لأنواع الفواتير الديناميكية
  const getInvoiceTypeIconConfig = (name) => {
    const trimmedName = name.trim();
    if (["بيع", "فاتورة بيع"].includes(trimmedName)) {
      return { icon: <ReceiptLongIcon />, color: "#4F46E5", bgColor: "rgba(79, 70, 229, 0.06)" };
    } else if (["مرتجع مبيعات", "فاتورة مرتجع مبيعات"].includes(trimmedName)) {
      return { icon: <ReplayIcon />, color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.06)" };
    } else if (["عرض سعر", "فاتورة عرض سعر"].includes(trimmedName)) {
      return { icon: <PriceCheckIcon />, color: "#06B6D4", bgColor: "rgba(6, 182, 212, 0.06)" };
    }
    return { icon: <ReceiptLongIcon />, color: "#4F46E5", bgColor: "rgba(79, 70, 229, 0.06)" };
  };

  // دالة عرض الأيقونات المطورة لتكون 4 عناصر كحد أقصى في الصف مرنة لجميع الشاشات والجوالات
  const renderGridItems = (items) => {
    return (
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',          // جوال: كرتين بالصف
            sm: 'repeat(3, 1fr)',          // تابلت: 3 كروت بالصف
            md: 'repeat(4, 1fr)',          // لابتوب: 4 كروت كحد أقصى
            lg: 'repeat(4, 1fr)',          // شاشات مكتبية: تثبيت 4 كروت بالصف
          },
          width: '100%'
        }}
      >
        {items.map((item, index) => (
          <ButtonBase
            key={index}
            onClick={() => navigate(item.link)}
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 3, sm: 4 },
              borderRadius: '24px', 
              backgroundColor: "#ffffff",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)',
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: `0 20px 25px -5px ${item.bgColor}, 0 10px 10px -5px rgba(0, 0, 0, 0.01)`,
                borderColor: item.color,
                "& .icon-container": {
                  transform: 'scale(1.08) rotate(3deg)',
                  backgroundColor: item.color,
                  color: '#ffffff'
                }
              },
              "&:active": { transform: "translateY(-1px)" }
            }}
          >
            {/* حاوية الأيقونة الدائرية التفاعلية */}
            <Box
              className="icon-container"
              sx={{
                width: { xs: 55, sm: 65 },
                height: { xs: 55, sm: 65 },
                borderRadius: '18px',
                backgroundColor: item.bgColor,
                color: item.color,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                transition: 'all 0.3s ease',
                '& svg': { fontSize: { xs: 26, sm: 30 } }
              }}
            >
              {item.icon}
            </Box>

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                color: '#1E293B',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                letterSpacing: '-0.3px',
                lineHeight: 1.4,
                px: 0.5
              }}
            >
              {item.name}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    );
  };

  // تصفية فواتير المبيعات وبناء الخصائص اللونية لها
  const salesInvoiceOptions = invoiceTypes
    .filter((type) => {
        const name = type.name.trim();
        const isSales = ["بيع", "فاتورة بيع"].includes(name);
        const isSalesReturn = ["مرتجع مبيعات", "فاتورة مرتجع مبيعات"].includes(name);
        const isQuotation = ["عرض سعر", "فاتورة عرض سعر"].includes(name);

        if (isSales) return hasPermission(PERMISSIONS.INVOICE_SALES_CREATE);
        if (isSalesReturn) return hasPermission(PERMISSIONS.INVOICE_SALES_RETURN_CREATE);
        if (isQuotation) return hasPermission(PERMISSIONS.INVOICE_QUOTATION_CREATE);

        return false;
    })
    .map((type) => {
      let translationKey = '';
      switch (type.name.trim()) {
        case "بيع":
        case "فاتورة بيع":
          translationKey = 'salesMenu.salesInvoice';
          break;
        case "مرتجع مبيعات":
        case "فاتورة مرتجع مبيعات":
          translationKey = 'salesMenu.salesReturn';
          break;
        case "عرض سعر":
        case "فاتورة عرض سعر":
          translationKey = 'salesMenu.quotation';
          break;
        default:
          translationKey = 'salesMenu.salesInvoice';
      }
      
      const config = getInvoiceTypeIconConfig(type.name);

      return {
        name: t(translationKey),
        icon: config.icon,
        color: config.color,
        bgColor: config.bgColor,
        link: `/invoices/new/${type.invoiceTypeId}`,
      };
    });

  // إعداد عناصر شاشة الفواتير والعمليات
  const invoiceSection = [
    ...salesInvoiceOptions,
    hasPermission(PERMISSIONS.INVOICE_SALES_CREATE) && {
      name: t('salesMenu.pos') || 'نقطة بيع',
      icon: <PointOfSaleIcon />,
      color: "#8B5CF6",
      bgColor: "rgba(139, 92, 246, 0.06)",
      link: "/pos"
    },
    hasPermission(PERMISSIONS.CUSTOMER_CREATE) && { 
      name: t('salesMenu.addCustomer'), 
      icon: <PersonAddIcon />, 
      color: "#10B981", 
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/customers/new" 
    },
    hasPermission(PERMISSIONS.CUSTOMER_VIEW) && { 
      name: t('salesMenu.customersList'), 
      icon: <PeopleIcon />, 
      color: "#059669", 
      bgColor: "rgba(5, 150, 105, 0.06)",
      link: "/customers" 
    },
  ].filter(Boolean);

  // إعداد شاشات التقارير
  const reportSection = [
    hasPermission(PERMISSIONS.INVOICE_SALES_VIEW) && {
      name: t('salesMenu.allSalesInvoices'),
      icon: <ListAltIcon />,
      color: "#2563EB",
      bgColor: "rgba(37, 99, 235, 0.06)",
      link: "/reports/all-invoices?direction=2&invoiceTypeId=1",
    },
    hasPermission(PERMISSIONS.INVOICE_SALES_RETURN_VIEW) && {
      name: t('salesMenu.allSalesReturns'),
      icon: <ListAltIcon />,
      color: "#3B82F6",
      bgColor: "rgba(59, 130, 246, 0.06)",
      link: "/reports/all-invoices?direction=2&invoiceTypeId=3",
    },
    hasPermission(PERMISSIONS.INVOICE_QUOTATION_VIEW) && {
      name: t('salesMenu.allQuotations'),
      icon: <ListAltIcon />,
      color: "#0284C7",
      bgColor: "rgba(2, 132, 199, 0.06)",
      link: "/reports/all-invoices?direction=0&invoiceTypeId=10",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.aggregatedItemsSalesReport'),
      icon: <SummarizeIcon />,
      color: "#0D9488",
      bgColor: "rgba(13, 148, 136, 0.06)",
      link: "/reports/aggregated-sales-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.detailedItemsSalesReport'),
      icon: <BarChartIcon />,
      color: "#10B981",
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/reports/detailed-sales-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.dailySalesReport'),
      icon: <CalendarTodayIcon />,
      color: "#F59E0B",
      bgColor: "rgba(245, 158, 11, 0.06)",
      link: "/reports/aggregated-sales-ByDay",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.financialTransactions'),
      icon: <CalendarTodayIcon />,
      color: "#D97706",
      bgColor: "rgba(217, 119, 6, 0.06)",
      link: "/LedgerReport/ledgers",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.VatReportPage'),
      icon: <PercentIcon />,
      color: "#EC4899",
      bgColor: "rgba(236, 72, 153, 0.06)",
      link: "/VatReportPage",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: "تقرير الفوترة الإلكترونية",
      icon: <FactCheckIcon />,
      color: "#4F46E5",
      bgColor: "rgba(79, 70, 229, 0.06)",
      link: "/reports/zatca",
    },
  ].filter(Boolean);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={45} thickness={4.5} sx={{ color: 'primary.main' }} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}>
          {t('salesMenu.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Paper sx={{ p: 3, borderRadius: '16px', textAlign: 'center', border: '1px solid rgba(220, 38, 38, 0.15)', bgcolor: 'rgba(220, 38, 38, 0.02)' }}>
          <Typography color="error" variant="body1" fontWeight={600}>
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 5, 
        mb: 5, 
        px: { xs: 2, md: 4 },
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* هيدر تدرج النصوص الفاخر والحديث بدون حواف مصمتة */}
      <Box sx={{ mb: 6, textAlign: { xs: 'center', md: 'start' } }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          sx={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            mb: 1
          }}
        >
          {t('salesMenu.dashboardTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          إدارة العملاء، فواتير المبيعات ونقاط البيع، معالجة المرتجعات، وإصدار تقارير الفوترة الإلكترونية وضريبة القيمة المضافة.
        </Typography>
      </Box>

      {/* قسم فواتير المبيعات */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          📋 {t('salesMenu.invoicesSection')}
        </Typography>
        {renderGridItems(invoiceSection)}
      </Box>
      
      {/* قسم تقارير المبيعات */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          📊 {t('salesMenu.reportsSection')}
        </Typography>
        {renderGridItems(reportSection)}
      </Box>
    </Container>
  );
}